import React, { useRef, useState } from 'react'

interface Props {
  elementWidth: number
  elementHeight: number
  onClick?: () => void
  children: React.ReactNode
}

interface Position {
  x: number
  y: number
}

const DraggableWrapper = ({ elementWidth, elementHeight, onClick, children }: Props): JSX.Element => {
  const [position, setPosition] = useState<'absolute' | 'fixed'>('absolute')
  const ref = useRef<HTMLDivElement>(null)
  const startPosition = useRef<Position | null>(null)
  const isDragging = useRef(false)

  const onTouchMove = (e: MouseEvent): void => {
    if (startPosition.current !== null) {
      const dx = e.clientX - startPosition.current.x
      const dy = e.clientY - startPosition.current.y

      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        isDragging.current = true
        if (ref.current !== null) {
          ref.current.style.left = `${e.clientX - elementWidth / 2}px`
          ref.current.style.top = `${e.clientY - elementHeight / 2}px`
        }
      }
    }
  }

  const onTouchStart = (e: React.MouseEvent): void => {
    setPosition('fixed')
    startPosition.current = { x: e.clientX, y: e.clientY }
    isDragging.current = false
    document.addEventListener('mousemove', onTouchMove)
    document.addEventListener('mouseup', onTouchEnd)
  }

  const onTouchEnd = (): void => {
    document.removeEventListener('mousemove', onTouchMove)
    document.removeEventListener('mouseup', onTouchEnd)

    if (!isDragging.current) {
      onClick?.()
    }

    startPosition.current = null
  }

  return (
    <div
      onMouseDown={onTouchStart}
      onMouseUp={onTouchEnd}
      className="absolute rounded-full cursor-grab select-none"
      ref={ref}
      style={{
        position,
        width: `${elementWidth}px`,
        height: `${elementHeight}px`
      }}>
      {children}
    </div>
  )
}

export default DraggableWrapper
